import React, { useEffect, useState } from "react"
import { Collapse, FormControl, Grid, InputAdornment, MenuItem, Radio, RadioGroup, FormControlLabel, Select, TextField, Typography } from "@material-ui/core"
import { PersonOutlined as UserIcon } from "@material-ui/icons"
import { Text, themeColors } from "letalkui"

import clsx from "clsx"

import { ActionDialog, Notification } from "@/components"

import useValidation, { ErrorType } from "@/hooks/useValidation"

import BoardStageCardService, { BoardCardStatus } from "@/services/BoardStageCardService"
import UserInInstanceService from "@/services/UserInInstance"
import { BoardLossReason, BOARD_CARD_STATUS_TO_LABEL, DEFAULT_BOARD_SETTINGS, BoardSettings } from "@/services/BoardService"
import ErrorHandlerService from "@/services/ErrorHandler"

import { UserInInstance } from "@/protocols/userInInstance"

import useMoneyInputCents from "@/hooks/useMoneyInputCents"

import RequiredFieldIndicator from "@/pages/Attendance/Chat/ConversationPanel/ClientInfo/ClientBoard/StatusConfirmationModal/RequiredFieldIndicator"
import useStyles from "@/pages/Attendance/Chat/ConversationPanel/ClientInfo/ClientBoard/StatusConfirmationModal/styles"

const LOSS_REASON_OTHER_ID = -1

type StatusConfirmationModalProps = {
	open: boolean
	boardId: number
	cardId: number
	cardName: string
	targetStageId: number
	status: BoardCardStatus
	lossReasons: Array<BoardLossReason>
	lossOtherEnabled: boolean
	settings?: BoardSettings
	responsibleUserId?: number
	contactValueInCents?: number
	onClose: () => void
	onUpdate: () => void
}

const StatusConfirmationModal: React.FC<StatusConfirmationModalProps> = (props) => {
	const {
		open,
		cardId,
		cardName,
		targetStageId,
		status,
		lossReasons,
		lossOtherEnabled,
		settings,
		responsibleUserId: initialResponsibleUserId,
		contactValueInCents,
		onClose,
		onUpdate
	} = props

	const classes = useStyles()

	const [isSaving, setSaving] = useState<boolean>(false)
	const [usersInInstance, setUsersInInstance] = useState<Array<UserInInstance>>([])
	const [responsibleUserId, setResponsibleUserId] = useState<number | undefined>(initialResponsibleUserId)
	const { cents: contactValueInCentsState, displayValue: contactValueDisplayValue, handleKeyDown: handleContactValueKeyDownFromHook, handlePaste: handleContactValuePasteFromHook, setCents: setContactValueCents } = useMoneyInputCents(contactValueInCents)
	const [selectedLossReasonId, setSelectedLossReasonId] = useState<number | null>(null)
	const [lossReasonCustomText, setLossReasonCustomText] = useState<string>("")

	const { validation, triggerValidation, clearAllValidations, addValidation } = useValidation()

	const resolvedSettings = { ...DEFAULT_BOARD_SETTINGS, ...settings }
	const isLostStatus = status === "lost"
	const isResponsibleUserRequired = resolvedSettings.isResponsibleUserRequired
	const isContactValueRequired = resolvedSettings.isContactValueRequired
	const isLossReasonRequired = isLostStatus

	const hasResponsibleUser = Boolean(responsibleUserId)
	const hasContactValue = contactValueInCentsState > 0
	const hasLossReason = selectedLossReasonId !== null
	const isOtherLossReasonSelected = selectedLossReasonId === LOSS_REASON_OTHER_ID
	const hasLossReasonCustomText = lossReasonCustomText.trim() !== ""

	const isResponsibleUserValid = !isResponsibleUserRequired || hasResponsibleUser
	const isContactValueValid = !isContactValueRequired || hasContactValue
	const isLossReasonValid = !isLossReasonRequired || (hasLossReason && (!isOtherLossReasonSelected || hasLossReasonCustomText))

	const canConclude = isResponsibleUserValid && isContactValueValid && isLossReasonValid

	const statusLabel = BOARD_CARD_STATUS_TO_LABEL[status]

	const statusToConcludeButtonClassName: Record<BoardCardStatus, string> = {
		won: classes.concludeButtonWon,
		lost: classes.concludeButtonLost
	}

	const handleChangeResponsibleUser = (userId: number) => {
		setResponsibleUserId(userId)
		clearAllValidations()
	}

	const handleChangeLossReason = (reasonId: number) => {
		setSelectedLossReasonId(reasonId)
		setLossReasonCustomText("")
		clearAllValidations()
	}

	const handleChangeLossReasonCustomText = (value: string) => {
		setLossReasonCustomText(value)
		clearAllValidations()
	}

	const handleContactValueKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		handleContactValueKeyDownFromHook(event)
		clearAllValidations()
	}

	const handleContactValuePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		handleContactValuePasteFromHook(event)
		clearAllValidations()
	}

	const handleConclude = async (): Promise<void> => {
		if (!canConclude) {
			return
		}

		if (isResponsibleUserRequired && !responsibleUserId) {
			addValidation({ responsibleUserId: "Selecione um atendente responsável." })
			return
		}

		if (isContactValueRequired && contactValueInCentsState === 0) {
			addValidation({ contactValueInCents: "Informe um valor para o contato." })
			return
		}

		setSaving(true)

		try {
			const resolvedLossReasonId = isLostStatus && !isOtherLossReasonSelected && selectedLossReasonId
				? selectedLossReasonId
				: undefined
			const resolvedLossReasonCustomText = isLostStatus && isOtherLossReasonSelected
				? lossReasonCustomText.trim()
				: undefined

			await BoardStageCardService.moveCardBetweenStages({
				cardId,
				targetStageId,
				responsibleUserId: responsibleUserId as number,
				contactValueInCents: contactValueInCentsState,
				lossReasonId: resolvedLossReasonId,
				lossReasonCustomText: resolvedLossReasonCustomText
			})

			onUpdate()

			Notification.success({ message: `Contato marcado como ${statusLabel}` })

			onClose()
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		setSaving(false)
	}

	const loadUsersInInstance = async (): Promise<void> => {
		try {
			const users = await UserInInstanceService.getUsersInInstance({
				type: ["human", "virtual-attendant"],
				status: ["active"]
			})

			if (users) {
				setUsersInInstance(users)
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	useEffect(() => {
		if (open) {
			loadUsersInInstance()
		}

		setResponsibleUserId(initialResponsibleUserId)
		setContactValueCents(contactValueInCents ?? 0)
		setSelectedLossReasonId(null)
		setLossReasonCustomText("")
		clearAllValidations()
	}, [open, initialResponsibleUserId, contactValueInCents])

	const renderResponsibleUserField = () => {
		return (
			<Grid item xs={12}>
				<Grid container direction="column" spacing={0.5}>
					<Grid item>
						<Grid container alignItems="center" spacing={1}>
							<Grid item>
								<Typography className={classes.fieldLabel}>
									Atendente responsável
								</Typography>
							</Grid>
							<Grid item>
								<RequiredFieldIndicator
									isFilled={isResponsibleUserValid}
									isRequired={isResponsibleUserRequired}
									label="Atendente responsável"
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<FormControl fullWidth variant="outlined" size="small" className={classes.fieldInput}>
							<Select
								variant="outlined"
								value={responsibleUserId ?? ""}
								onChange={(event) => handleChangeResponsibleUser(event.target.value as number)}
								fullWidth
								displayEmpty
								classes={{ select: classes.responsibleUserSelectInput }}
								startAdornment={(
									<InputAdornment position="start">
										<UserIcon />
									</InputAdornment>
								)}
								renderValue={(selectedUserId) => {
									const selectedUser = usersInInstance.find(user => user.id === selectedUserId)

									if (!selectedUser) {
										return (
											<span className={classes.responsibleUserPlaceholder}>
												Selecionar o atendente
											</span>
										)
									}

									return selectedUser.name
								}}
							>
								{usersInInstance.map((user) => (
									<MenuItem key={user.id} value={user.id}>
										{user.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
			</Grid>
		)
	}

	const renderContactValueField = () => {
		return (
			<Grid item xs={12}>
				<Grid container direction="column" spacing={0.5}>
					<Grid item>
						<Grid container alignItems="center" spacing={1}>
							<Grid item>
								<Typography className={classes.fieldLabel}>
									Valor do contato
								</Typography>
							</Grid>
							<Grid item>
								<RequiredFieldIndicator
									isFilled={isContactValueValid}
									isRequired={isContactValueRequired}
									label="Valor do contato"
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<TextField
							className={classes.fieldInput}
							value={contactValueDisplayValue}
							variant="outlined"
							size="small"
							type="text"
							inputMode="decimal"
							fullWidth
							placeholder="0,00"
							InputProps={{
								readOnly: true,
								startAdornment: (
									<InputAdornment position="start">
										R$
									</InputAdornment>
								)
							}}
							onKeyDown={handleContactValueKeyDown}
							onPaste={handleContactValuePaste}
							error={Boolean(validation?.contactValueInCents)}
							helperText={validation?.contactValueInCents as string}
						/>
					</Grid>
				</Grid>
			</Grid>
		)
	}

	const renderLossReasonField = () => {
		if (!isLostStatus) {
			return null
		}

		const lossReasonOptions = lossOtherEnabled
			? [...lossReasons, { id: LOSS_REASON_OTHER_ID, reasonText: "Outro" }]
			: lossReasons

		return (
			<Grid item xs={12} className={classes.lossReasonSection}>
				<Grid container direction="column" spacing={1}>
					<Grid item>
						<Typography className={classes.lossReasonTitle}>
							Motivo da perda
						</Typography>
					</Grid>
					<Grid item>
						<RadioGroup
							value={selectedLossReasonId ?? ""}
							onChange={(_, value) => handleChangeLossReason(Number(value))}
						>
							<Grid container direction="column" spacing={1} wrap="nowrap" className={classes.lossReasonsList}>
								{lossReasonOptions.map((reason) => {
									const isSelected = selectedLossReasonId === reason.id

									return (
										<Grid item key={reason.id}>
											<FormControlLabel
												value={reason.id}
												control={(
													<Radio
														size="small"
														className={classes.lossReasonRadio}
													/>
												)}
												label={reason.reasonText}
												className={clsx(classes.lossReasonOption, isSelected ? classes.lossReasonOptionSelected : classes.lossReasonOptionUnselected)}
											/>
										</Grid>
									)
								})}
							</Grid>
						</RadioGroup>
					</Grid>
					{validation?.lossReasonId && (
						<Grid item>
							<Typography className={classes.errorText}>
								{validation.lossReasonId as string}
							</Typography>
						</Grid>
					)}
					<Grid item xs={12}>
						<Collapse in={isOtherLossReasonSelected}>
							<TextField
								placeholder="Descreva o motivo"
								value={lossReasonCustomText}
								onChange={(event) => handleChangeLossReasonCustomText(event.target.value)}
								variant="outlined"
								fullWidth
							/>
						</Collapse>
					</Grid>
				</Grid>
			</Grid>
		)
	}

	return (
		<ActionDialog
			title={`Concluir como ${statusLabel}`}
			openDialog={open}
			loading={isSaving}
			onClose={onClose}
			onSave={handleConclude}
			saveText="Concluir"
			cancelText="Cancelar"
			saveButtonDisabled={!canConclude}
			saveButtonProps={{ className: statusToConcludeButtonClassName[status] }}
			fullWidth
			maxWidth="sm"
		>
			<Grid container direction="column" spacing={2}>
				<Grid item xs={12}>
					<Text.Body
						type="small1"
						color={themeColors.colors.system.gray[600]}
					>
						Confirme os dados abaixo para concluir a oportunidade de <b>{cardName}</b>.
					</Text.Body>
				</Grid>
				{renderResponsibleUserField()}
				{renderContactValueField()}
				{renderLossReasonField()}
			</Grid>
		</ActionDialog>
	)
}

export default StatusConfirmationModal
